/* These mixins are designed to work with AP/PC/JE transactions (both New and Edit)
 * Assumes parent component has all necessary data elements
 * e.g. Vuex store, ElementUI, etc.
*/
/* eslint-disable camelcase */

import { mapGetters } from 'vuex'
import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
import { validateDataEntryModels } from '@/utils/helpers/Validate'
import { DateFormatter, formatDateFields, getSimpleDate } from '@/utils/helpers/FormatDate'
import { cleanupLines } from '@/utils/helpers/CheckDistributionLines'
import prepareFormData from '@/utils/helpers/PrepareFormData'
import { formatDistributionLineMoneyFields } from '@/utils/helpers/DataEntryViews/FormatMoneyFields'
import { tabThruModalButtons } from '@/utils/helpers/KeyNavHelper'
import { payload as accountActionPayload } from '@/components/RouteComponents/Accounts/actionPayload'
import { payload as seriesActionPayload } from '@/components/RouteComponents/Series/actionPayload'
import { payload as locationActionPayload } from '@/components/RouteComponents/Locations/actionPayload'
import { payload as setActionPayload } from '@/components/RouteComponents/Sets/actionPayload'
import { payload as freefieldActionPayload } from '@/components/RouteComponents/Freefields/actionPayload'
import { payload as taxcodeActionPayload } from '@/components/RouteComponents/Taxcodes/actionPayload'
import { payload as insurancecodeActionPayload } from '@/components/RouteComponents/Insurances/actionPayload'
import { payload as sourcecodeActionPayload } from '@/components/RouteComponents/Sourcecodes/actionPayload'
import AutocompleteLocalSearch from '@/utils/helpers/Search/AutocompleteLocalSearch'
import { getHeaders } from '@/utils/api'
import Axios from 'axios'
import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'
import autoNumber from '@/utils/helpers/DataEntryViews/AutoNumber'
import moment from 'moment'

export const transactionMixins = {
  data () {
    return {
      effectiveDateMandatory: false,
      exitWarningDialogShown: false,
      showExitWarningDialog: false,
      isSaving: false,
      currentVendorSelection: '',
      vendorNumberVerifier: '',
      vendorNumberCheckTimeout: null,
      journalNumberCheckTimeout: null,
      trxAlreadyExists: false,
      trxAlreadyExistsMsg: null
    }
  },
  methods: {
    paymentDateValidation (invoiceDate, paymentDate, action) {
      if (invoiceDate && paymentDate && action === 'blur') {
        if (moment(paymentDate).isBefore(moment(invoiceDate))) {
          this.$message({
            showClose: true,
            message: 'Payment date must be equal to or after the transaction date.',
            type: 'error'
          })
        }
      }
      return
    },
    /**
     * Enables or disables readOnly mode, which determines whether certain header fields and
     * distribution lines can be changed.
     */
    toggleReadOnly () {
      this.readOnly = !this.readOnly
    },
    /**
     * Cancels exit if the exit confirmation dialog is showing and the user hits "Cancel"
     */
    cancelExit () {
      this.altRoute = null
      this.handleDialogVisible(false)
    },
    /**
     * Governs visibility of the exit confirmation modal that displays when the user hits the browser back
     * button while a transaction is saving
     * @param visible
     */
    handleExitWarningDialogVisible (visible) {
      this.showExitWarningDialog = visible
      this.exitWarningDialogShown = visible
    },
    confirmSaveBeforeExit () {
      this.exitWarningDialogShown = true
      this.submitForm(false, null, true)
    },
    /**
     * Governs whether exit confirmation modal is visible
     * @param visible
     */
    handleDialogVisible (visible) {
      this.dialogVisible = visible
      tabThruModalButtons(visible ? 'exit-modal-btn' : null)
    },
    /**
     * Checks for changes by determining whether anything exists in this.headerDeltas or this.lineDeltas.
     */
    checkForChanges () {
      this.hasBeenChanged = !!(Object.keys(this.headerDeltas).length + Object.keys(this.lineDeltas).length)
    },
    /**
     * Formats dollar amounts for a specific distribution line field
     * @param coordinates
     */
    formatLineMoneyField (coordinates) {
      formatDistributionLineMoneyFields(this, coordinates)
    },
    /**
     * Formats dates to YYYY-MM-DD format
     */
    formatDateFields (fieldName) {
      formatDateFields(this, fieldName)
    },
    /**
     * Determines what happens when certain options in the "Save" dropdown menu are clicked
     * @param c Option identifier
     */
    handleDropdownClick (c) {
      const { handleSubmit } = this
      let loaderText = 'Saving...'
      if (c === '3') loaderText = 'Auditing transaction...'
      if (c === '4') loaderText = 'Posting transaction...'
      this.showTrxActionLoader(loaderText)
      switch (c) {
        case '1':
          this.originalHeader.status === 'DRAFT' && this.transactionIsBalanced
            ? handleSubmit(true, { status: 'OPEN' }) // Save and open if draft and balanced
            : handleSubmit(true) // Save only
          break
        case '2': // Save & keep working (if transaction is balanced and status is not draft)
          handleSubmit(true, { status: 'OPEN' })
          break
        case '3': // Save & audit
          handleSubmit(true, null, 'audit')
          break
        case '4': // Save & post
          handleSubmit(true, null, 'post')
      }
    },
    /**
     * Opens a blurred loader scoped to the transaction container and auto-closes it
     * when isSaving / disablePostBtn / disableAuditBtn all settle back to false.
     * Returns the close handle so the caller can force-close it early if needed.
     * @param text   The text shown next to the spinner
     */
    showTrxActionLoader (text) {
      const loader = this.$loading({
        lock: true,
        fullscreen: true,
        text,
        customClass: 'trx-action-loader'
      })
      let closed = false
      const closeLoader = () => {
        if (closed) return
        closed = true
        loader.close()
      }
      const settled = () => !this.isSaving && !this.disablePostBtn && !this.disableAuditBtn
      const unwatch = this.$watch(
        function () {
          return [this.isSaving, this.disablePostBtn, this.disableAuditBtn]
        },
        function () {
          if (settled()) {
            unwatch()
            closeLoader()
          }
        }
      )
      this.$nextTick(() => {
        if (settled()) {
          unwatch()
          closeLoader()
        }
      })
      return closeLoader
    },
    /**
     * Function that allows the user to directly add a new transaction from
     * the edit view for a given transaction type. Assuming that this
     * function is only used with the + buttons in transaction edit views,
     * it parses the route name, changes it to a valid transaction create view route
     * name, and starts the navigation process.
     */
    addNew () {
      const newRoute = this.$route.name.replace('Edit', 'New')
      if (this.form.batch && this.form.batch.value) {
        this.$router.push({ name: newRoute, query: { batch: this.form.batch.value } })
      } else {
        this.$router.push({ name: newRoute })
      }
    },
    /**
     * Generates an alternate route depending on whether to the to argument contains a route
     * name and if it contains a query.
     * @param to
     * @return {Object}
     */
    generateAltRoute (to) {
      if (to && to.name && to.query) {
        return { name: to.name, query: to.query }
      }
      return { name: to.name }
    },
    getMethodName (type) {
      switch (type) {
        case 'ap':
          return 'fetchCompactLineApsWithQuery'
        case 'je':
          return 'fetchCompactLineJesWithQuery'
        case 'pc':
          return 'fetchCompactLinePcsWithQuery'
        case 'pr':
          return 'fetchCompactLineJesWithQuery'
      }
    },
    /**
     * This is for the "Other Actions" dropdown
     * @param action
     * @return {boolean}
     */
    async otherActions (action = null) {
      const validActions = ['toggleDistChanges', 'reimbursePcVendor', 'duplicateTransaction', 'uploadDocument', 'void', 'delete', 'postReportBtnClick', 'unPostTransaction']
      if (!action || !validActions.includes(action)) return false
      switch (action) {
        case 'toggleDistChanges':
          let type = this.transactionType.name
          if (this.readOnly && type !== 'po') {
            this.fetchingPoData = true
            let trxId = this.$route.params.id
            let transactionLines = []
            let customLines = await this.$store.dispatch(this.getMethodName(type), { query: { id: this.originalHeader.id, compact_lines: true }, storeLines: true })
            if (customLines && customLines.entities && customLines.entities.lines) {
              transactionLines = Object.values(customLines.entities.lines)
            }
            let offsetResponse = transactionLines
              .filter(v => v.offset_by)
              .map(v => this.$store.getters.getJeLineById(v.offset_by))
            if (offsetResponse && offsetResponse.length) {
              let jes = []
              offsetResponse.forEach(line => jes.push(line.transaction_id))
              jes = [...new Set(jes)]
              await Promise.all(jes.map(id => {
                return this.$store.dispatch('fetchJeById', {id, storeLines: true})
                  .then((r) => {
                    if (r) {
                      const jeLines = Object.values(this.$store.getters.getJeLinesByTransactionId(id))
                      this.$store.dispatch('findDependencies', {lines: jeLines})
                    }
                  })
              }))
              .then(() => {
                setTimeout(() => {
                  const newLines = this.buildLinesWithDcs(Object.values(this.$store.getters[`get${capitalizeFirstLetter(type)}LinesByTransactionId`](trxId)))
                  this.originalLines = newLines
                  this.existingLines = newLines
                  if (this.$refs && this.$refs['distribution-lines']) {
                    this.$refs['distribution-lines'].rebuildLines()
                  }
                  this.toggleReadOnly()
                  this.fetchingPoData = false
                }, 3000)
              })
            } else {
              this.toggleReadOnly()
              this.fetchingPoData = false
            }
          } else {
            this.toggleReadOnly()
          }
          break
        case 'reimbursePcVendor': // Adding a new Invoice to reimburse negative balance on a PC Vendor
          try {
            const confirmation = await this.createConfirmationBox('Confirm', 'Create an A/P Reimbursement for this Vendor?')
            if (confirmation) {
              this.addTrx('ap', this.buildReimbursementTransaction())
              this.form.reimbursed_amount_decimal = this.form.amount_decimal
              this.updateForm(this.form, 'reimbursed_amount_decimal')
              this.handleSubmit(true)
              return true
            } else {
              this.$notify({ title: 'Message', message: 'New A/P Transaction not created.', type: 'error' })
              return false
            }
          } catch (e) {
            console.log(e)
          }
          break
        case 'duplicateTransaction':
          try {
            const confirmation = await this.createConfirmationBox('Confirm', 'Duplicate this Transaction?')
            if (confirmation) {
              this.isSaving = true // keep loader open across the async buildDuplicateTransaction below
              this.showTrxActionLoader('Duplicating transaction...')
              const duplicateTransactionObject = await this.buildDuplicateTransaction()
              this.addTrx(this.transactionType.name, duplicateTransactionObject, action)
              return true
            } else {
              this.$notify({ title: 'Message', message: 'Unable to generate duplicate transaction', type: 'error ' })
              return false
            }
          } catch (e) {
            this.isSaving = false
            console.log(e)
          }
          break
        case 'uploadDocument':
          this.dropzoneVisible = true
          break
        case 'void':
          try {
            let hide = false
            if (this.transactionType.name === 'ar' && typeof this.handleVoidingAr === 'function') {
              hide = await this.handleVoidingAr()
            }
            if (!hide) {
              const confirmation = await this.createConfirmationBox('Confirm', 'Void this Transaction?')
              if (!confirmation) {
                return true // User canceled voiding
              }
              this.isSaving = true // keep loader open across the async dc-je void work below
              this.showTrxActionLoader('Voiding transaction...')
              const dcJeIds = await this.findCorrespondingDcJes()
              if (dcJeIds.length) {
                const voidingSuccessful = await this.voidDcJes(dcJeIds)
                if (!voidingSuccessful) {
                  throw new Error('Unable to void corresponding distribution change Journal Entries')
                }
              }
              this.handleSubmit(true, { status: 'VOIDED' })
            }
          } catch (e) {
            this.isSaving = false
            console.log(e)
            this.$notify({ title: 'Error voiding transaction', message: e.message, type: 'error' })
          }
          break
        case 'delete':
          try {
            const confirmation = await this.createConfirmationBox('Confirm', 'Delete this Transaction?')
            if (confirmation) {
              this.showTrxActionLoader('Deleting transaction...')
              this.handleSubmit(true, { status: 'DELETED' })
            }
          } catch (e) {
            console.log(e)
          }
          break
        case 'postReportBtnClick':
          try {
            this.handlePostReportBtnClick(this.transactionType.prettyName)
          } catch (e) { console.log(e) }
          break
        case 'unPostTransaction':
          try {
            const confirmation = await this.createConfirmationBox('Confirm', 'Unpost this Transaction?')
            if (confirmation) {
              this.$store.dispatch('unPostPcTransaction', {id: this.$route.params.id})
                .then((r) => {
                  if (r && r.message) {
                    this.$notify({ title: 'Success', message: r.message, type: 'success' })
                    const payload = { query: { id: this.originalHeader.id }, storeLines: true }
                    this.$store.dispatch('fetchPcsWithQuery', payload)
                  }
                })
            }
          } catch (e) {
            console.log(e)
          }
          break
        default:
          return false
      }
    },
    /**
     * Confirmation Dialog box
     * @param title
     * @param message
     * @param type
     * @return {Promise}
     */
    createConfirmationBox (title, message, type) {
      return new Promise((resolve, reject) => {
        if (!message) message = 'Confirm Action?'
        const newEl = this.$createElement
        message = newEl('p', null, [
          newEl('span', null, message + ' '),
          newEl('button',
            {
              attrs: { class: 'dialog-button' },
              on: {
                click: () => {
                  this.notificationBox.close()
                  return resolve(false)
                }
              }
            },
            'No' // Button label
          ),
          newEl('button',
            {
              attrs: { class: 'dialog-button button-success' },
              on: {
                click: () => {
                  this.notificationBox.close()
                  return resolve(true)
                }
              }
            },
            'Yes' // Button label
          )
        ])
        this.notificationBox = this.$message({
          title: title || 'Message',
          type: type || 'info',
          duration: 0,
          message,
          showClose: true
        })
      })
    },
    /**
     * New Transaction popup box
     * @param title
     * @param message
     * @param type
     * @param url
     */
    showNewTransactionBox (title, message, type, url) {
      if (!message) message = 'New Transaction Created. '
      if (url) {
        const newEl = this.$createElement
        message = newEl('p', null, [
          newEl('span', null, message + ' '),
          newEl('button', { on: { click: () => { this.openTransaction(url, '_blank') } } }, 'View')
        ])
      }
      this.notificationBox = this.$message({
        title: title || 'Message',
        type: type || 'info',
        duration: 0,
        message,
        showClose: true
      })
    },
    /**
     * Helper function to launch the report in a different tab
     * @param url
     * @return {boolean}
     */
    openTransaction (url) {
      if (!url) return false
      this.notificationBox.close()
      window.location.href = url
      // this.$router.push(url)
    },
    /**
     * Creates a duplicate transaction
     * @param type
     * @param transactionObject
     * @return {Promise}
     */
    async addTrx (type, transactionObject, action = null) {
      try {
        if (!['ap', 'je', 'pc', 'po', 'ar', 'bp', 'pp'].includes(type)) {
          throw new Error()
        } else if (typeof transactionObject !== 'object' ||
          !transactionObject.hasOwnProperty('header') ||
          typeof transactionObject.header !== 'object' ||
          !transactionObject.hasOwnProperty('lines') ||
          !Array.isArray(transactionObject.lines)) {
          throw new Error()
        }
        this.isSaving = true
        this.$notify({title: 'Message', message: 'Creating new transaction. Please wait a moment.', type: 'info'})
        const lines = this.saveNewTrxLines(transactionObject.lines)
        const resp = await this.saveNewTrxHeader(type, {...transactionObject.header, lines})
        if (!(resp && resp.id)) {
          throw new Error('Unable to create transaction. Please try again.')
        }
        let url = `/${type === 'pc' ? 'pettycash' : type + 's'}/${resp.id}`
        if (type === 'je' && transactionObject.header.source_code && this.$store.getters.getSourcecodeById(transactionObject.header.source_code).code === 'PR') {
          url = `/prs/${resp.id}`
        }
        this.showNewTransactionBox('Success!', 'Transaction Created as a DRAFT.', 'success', url)
        this.isSaving = false
        if (action === 'duplicateTransaction') {
          sessionStorage.removeItem('paginationInfo')
          let sessionStorageQuery = sessionStorage.getItem('paginationQuery')
          if (sessionStorageQuery) {
            let paginationQuery = JSON.parse(sessionStorageQuery)
            if (paginationQuery && paginationQuery['page']) {
              paginationQuery['page'] = 1
              sessionStorage.setItem('paginationQuery', JSON.stringify(paginationQuery))
            }
          }
        }
      } catch (e) {
        this.isSaving = false
        if (e instanceof Error) {
          this.$notify({ title: 'Error', message: e.message, type: 'error' })
        }
        return false
      }
    },
    /**
     * Saves a duplicate transaction header
     * @param type
     * @param headerData
     * @return {Promise}
     */
    async saveNewTrxHeader (type, headerData) {
      try {
        const addAction = 'save' + capitalizeFirstLetter(type)
        // For JEs whose number sequence is USER_DEFINED or LEDGER_ID, the number is assigned
        // outside of this form (cleared in buildDuplicateTransaction and set by the user/ledger),
        // so the "number" field should not be validated as required. Clone the affected fields
        // branch and clear the required flag without mutating the shared `this.fields` definition.
        let fields = this.fields
        if (type === 'je' && !['SYSTEM_ASSIGNED', 'BLANK'].includes(this.$store.getters.getUiSettings['JE_NUMBER_SEQUENCE'])) {
          fields.jeData.inputFields.forEach(f => {
            if (f.name === 'number') f.required = false
          })
        }
        const validationErrors = validateDataEntryModels(headerData, fields)
        if (validationErrors.messages) {
          throw new Error(validationErrors.messages)
        }
        const dateFormatter = new DateFormatter({ fields: this.fields, form: headerData })
        headerData = dateFormatter.formatDateFormFields()
        headerData.status = 'DRAFT'
        headerData.period = this.$store.getters.getCurrentPeriod.id
        const resp = await this.$store.dispatch(addAction, prepareFormData(headerData))
        if (!(resp && resp.id)) {
          throw new Error('Error saving ' + type)
        }
        return resp
      } catch (e) {
        console.log(e)
        return e
      }
    },
    /**
     * Saves duplicated transaction lines
     * @param type
     * @param id
     * @param lines
     * @param stripReliefLines
     * @param stripApOffsetLines
     * @return {Promise}
     */
    saveNewTrxLines (lines, stripReliefLines = true, stripApOffsetLines = true) {
      try {
        if (stripApOffsetLines === true && lines.length > 0) { // Remove AP Offset Lines
          lines = lines.filter(line => {
            return (line && line.form) ? !line.form.description.match('A/P Offset,.*TRX #') : !line.description.match('A/P Offset,.*TRX #')
          })
        }
        for (const i in lines) { // remove relief lines and existing line ids
          if (lines[i].hasOwnProperty('id')) lines[i].id = null
          if (stripReliefLines === true && lines[i].hasOwnProperty('relieves')) lines[i].relieves = null
        }
        return lines
      } catch (e) {
        console.log(e)
        this.isSaving = false
      }
    },
    /**
     * Add the transaction header  // TODO: deprecated
     * @param type
     * @param headerData
     * @return {Promise}
     */
    saveNewTransactionHeader (type, headerData) {
      return new Promise((resolve, reject) => {
        const addAction = 'save' + capitalizeFirstLetter(type)
        const validationErrors = validateDataEntryModels(headerData, this.fields)
        if (validationErrors.messages) reject(validationErrors.messages)
        const dateFormatter = new DateFormatter({ fields: this.fields, form: headerData })
        headerData = dateFormatter.formatDateFormFields()
        headerData.status = 'DRAFT'
        headerData.period = this.$store.getters.getCurrentPeriod.id
        this.$store.dispatch(addAction, prepareFormData(headerData))
          .then(responseNewTrans => {
            if (!responseNewTrans || !responseNewTrans.id) reject(this, 'error', 'Error saving ' + type)
            else resolve(responseNewTrans.id)
          })
          .catch(error => {
            console.log(error)
            return reject('Error saving ' + type)
          })
      })
    },
    /**
     * Helper function to lookup the account code in the store or retrieve it if not there
     * @param accountId
     * @return {Promise}
     */
    lookupAccountCode (accountId) {
      if (!accountId) return false
      const account = this.$store.getters.getAccountById(accountId)
      if (account.hasOwnProperty('id')) return account
      return this.$store.dispatch('fetchAccountsWithQuery', { id__in: accountId })
        .then(response => {
          if (response.hasOwnProperty('count') && response.count === 1 && response.entities.hasOwnProperty('accounts')) {
            return response.entities.accounts[accountId]
          }
          return false
        })
        .catch(error => {
          console.log(error)
          return false
        })
    },
    /**
     * Function to duplicate a new transaction based on the current one
     * @return {{header, lines: *}}
     */
    async buildDuplicateTransaction () {
      let duplicateForm = Object.assign({}, this.form)
      duplicateForm = Object.assign({ payment_confirmation_number: null, payment_confirmation_number_duplicate_accepted: true }, duplicateForm)
      let headerPropertiesToExclude = ['payment_confirmation_number', 'bank_transaction_number', 'payment_date', 'offset_by_id', 'recon_id', 'meta_data']
      if (duplicateForm && duplicateForm.payment_type === 'CHECK_MANUAL') duplicateForm['payment_type'] = 'CHECK'
      headerPropertiesToExclude.forEach(prop => delete duplicateForm[prop])
      const header = prepareFormData(duplicateForm)
      // Don't duplicate certain data. So reset or modify the properties on the transaction and the lines.
      const propertiesToAddPrefix = { 'description': 'DUPLICATE OF', 'number': 'DUPLICATE OF' }
      let propertiesToChange = {
        'id': null,
        'time_posted': null,
        'ledger': null,
        'period': null,
        'export': null,
        'files': null,
        'date_due': null,
        'payment_confirmation_number': null,
        'payment_date': null
      }
      if (!this.effectiveDateMandatory) {
        propertiesToChange = { ...propertiesToChange, effective_date: null }
      }
      const linePropertiesToRemove = [
        'id',
        'account_code',
        'account_name',
        'account_type',
        'transaction_id',
        'ledger_id',
        'transaction_date',
        'time_posted',
        'relieves',
        'relieves_force_close',
        'offset_by',
        'recon_id',
        'recon',
        'referencing_line_id',
        'export_id',
        'meta_data',
        'meta_name',
        'meta_type',
        'created',
        'updated'
      ]
      for (const prop in propertiesToAddPrefix) {
        if (header.hasOwnProperty(prop) && header[prop]) {
          header[prop] = propertiesToAddPrefix[prop] + ' ' + header[prop]
        }
      }
      // Resolve the duplicated JE's number based on the configured numbering sequence:
      //  - USER_DEFINED / LEDGER_ID: clear it so it is assigned by the user/ledger on save
      //  - SYSTEM_ASSIGNED: generate a fresh auto number now
      //  - BLANK (or non-JE): keep the "DUPLICATE OF ..." value set above
      let JeNumberSequence = this.transactionType.name === 'je' ? this.$store.getters.getUiSettings['JE_NUMBER_SEQUENCE'] : null
      if (JeNumberSequence && ['USER_DEFINED', 'LEDGER_ID'].includes(JeNumberSequence)) header['number'] = ''
      else if (JeNumberSequence === 'SYSTEM_ASSIGNED') {
        header['number'] = autoNumber()
      }
      for (const prop in propertiesToChange) {
        if (header.hasOwnProperty(prop)) {
          header[prop] = propertiesToChange[prop]
        }
      }
      let originalLines = this.lines
      if (this.transactionType && this.transactionType.name === 'ap') {
        originalLines = this.lines.filter(line => !line.form.is_offset)
      }
      const lines = cleanupLines(originalLines).map(line => {
        const newLine = line.form || line
        let lineNumber = (typeof lineNumber === 'undefined') ? 1 : lineNumber++
        for (const prop in newLine) {
          if (linePropertiesToRemove.indexOf(prop) > -1) {
            delete newLine[prop]
          }
        }
        return newLine
      })
      return { header, lines }
    },
    // Function to setup a dropzone instance
    setupDropzoneOptions () {
      const instance = this.$store.getters.getActiveInstance
      const instanceUrl = (instance.url.endsWith('/') ? instance.url.slice(0, -1) : instance.url)
      const path = this.$route.path.includes('pettycash') ? `/pcs/${this.$route.params.id}` : this.$route.path
      return {
        instanceUrl,
        url: instanceUrl + path + '/files',
        headers: '',
        signingUrl: instanceUrl + path + '/files',
        signingHeaders: { 'Authorization': 'Bearer ' + instance.token },
        acceptedFiles: '.txt,.pdf,.jpg,.png,.tif,.tiff,.doc,.docx,.xls,.xlsx',
        autoProcessQueue: false,
        maxFileSize: 8,
        uploadMultiple: false,
        parallelUploads: 1
        // maxFiles: 5,
        // thumbnailHeight: 20,
        // thumbnailWidth: 20
      }
    },
    getSignedFile () {
    },
    dzSuccess () {
      // console.log('Success!')
      this.numberOfFiles = 1
    },
    dzFailure () {
      console.log('Success! just kidding')
    },
    dzRemove () {
      console.log('File removed')
      this.numberOfFiles = -1
    },
    /**
     * Retrieves list of vendor open POs
     */
    async getPoList () {
      this.poList = []
      let vendor = this.getVendorById(this.form.vendor.value)
      if (!vendor.id) {
        await this.$store.dispatch('fetchVendorById', this.form.vendor.value)
        vendor = this.getVendorById(this.form.vendor.value)
      }
      this.fetchingPoData = true
      try {
        const headers = getHeaders(this.$store.getters.getToken)
        const instanceUrl = this.$store.getters.getActiveInstance.url
        const pageSize = 20

        await Axios.get(`${instanceUrl}pos/po_specific_columns`, {
          params: {
            vendor: vendor.id,
            status__in: 'POSTED,PARTIAL',
            page_size: pageSize
          },
          headers
        })
        .then(async (response) => {
          if (response.status && response.status >= 200 && response.status < 300) {
            let { data } = response
            this.poList = this.processResults(data.results)
            if (data.count > pageSize) {
              let pageCount = Math.ceil(data.count / pageSize)
              const pcIter = {
                [Symbol.asyncIterator] () {
                  return {
                    i: 1,
                    next () {
                      if (this.i < pageCount) {
                        return Promise.resolve({ value: this.i++, done: false })
                      }
                      return Promise.resolve({ done: true })
                    }
                  }
                }
              }
              let results = []
              for await (let page of pcIter) {
                await Axios.get(`${instanceUrl}pos/po_specific_columns`, {
                  params: {
                    vendor: vendor.id,
                    status__in: 'POSTED,PARTIAL',
                    page_size: pageSize,
                    page: (page + 1)
                  },
                  headers
                })
                .then((response) => {
                  if (response.status && response.status >= 200 && response.status < 300) {
                    const { data } = response
                    if (data.count > 0) {
                      results.push(...data.results)
                    }
                    if (page === pageCount - 1) {
                      this.poList.push(...this.processResults(results))
                      this.fetchingPoData = false
                    }
                  }
                })
                .catch((error) => {
                  console.log(error)
                })
              }
            } else {
              this.fetchingPoData = false
            }
          }
        })
      } catch (e) {
        console.log(e)
        this.fetchingPoData = false
      }
    },

    processResults (results) {
      const pos = []
      for (const result of results) {
        const poLines = []
        for (const line of result.lines) {
          if (line.type === 'OPEN' || line.type === 'PARTIAL') {
            poLines.push(line)
          }
        }
        const tempPo = result
        tempPo.lines = poLines
        pos.push(tempPo)
      }
      return pos
    },
    /**
     * Creates options for 'relieves' field whenever `vendorPoLines` changes (which in-turn would be caused when the
     * vendor is changed in the header). This helps ensure that, if the vendor is changed after the transaction is created
     * (and relief lines are selected), then the existing PO line references are not removed (which would otherwise cause
     * whatever PO lines aren't associated with the new vendor to not show up).
     */
    createRelievesFieldOptions () {
      this.reliefFieldOpts = []
      const availablePoLines = this.vendorPoLines && this.vendorPoLines.length ? this.vendorPoLines : []
      if (!['po', 'ar', 'bp', 'pp'].includes(this.entityType)) {
        const availIds = this.vendorPoLines && this.vendorPoLines.length ? this.vendorPoLines.map(v => v.id) : []
        const setIds = this.lines.filter(v => v.form && v.form.relieves).map(v => v.form.relieves)
        const diff = setIds.filter(v => !availIds.includes(v))
        diff.forEach(id => availablePoLines.push(this.$store.getters.getPoLineById(id)))
      }
      this.reliefFieldOpts = availablePoLines
    },
    /**
     * Clear whatever line fields are specified in "fieldsToClear" argument
     */
    clearLineFields (fieldsToClear = []) {
      if (!(fieldsToClear && Array.isArray(fieldsToClear))) {
        throw new TypeError(`Invalid argument passed, expected Array, got ${typeof fieldsToClear}.`)
      }
      if (!fieldsToClear.every(field => typeof field === 'string')) {
        throw new Error(`Invalid contents in fieldsToClear argument: field names must be strings.`)
      }
      this.lineFieldsToClear = fieldsToClear
      setTimeout(() => (this.lineFieldsToClear = []))
    },
    handleDuplicatepayments (msg, edit = false) {
      this.createConfirmationBox('Confirm', msg)
      .then(confirmation => {
        if (confirmation === true) {
          if (edit) {
            this.headerDeltas.payment_confirmation_number_duplicate_accepted = true
            if (this.$route.name === 'EditAp') {
              this.handleDropdownClick('1')
            } else this.handleSubmit()
          } else {
            this.form.payment_confirmation_number_duplicate_accepted = true
            this.handleDropdownClick('1')
          }
        } else if (confirmation === false) {
          return false
        }
      })
      .catch(error => {
        console.log(error)
      })
    },
    /**
     * Checks whether a transaction with this vendor and number already exists, and, if so,
     * displays a notification that allows the user to discard this transaction and navigate to the
     * existing one.
     */
    async checkIfTrxExists () {
      clearTimeout(this.vendorNumberCheckTimeout)
      const { vendor, number, customer } = this.form
      const data = { conditionsMet: false, action: null }
      const { name } = this.transactionType
      switch (this.transactionType.name) {
        case 'ap':
          data.conditionsMet = (vendor.value && number)
          data.action = async () => this.$store.dispatch('fetchApsWithQuery', { vendor: vendor.value, number })
          break
        case 'po':
          data.conditionsMet = !!number
          data.action = async () => this.$store.dispatch('fetchPosWithQuery', { number })
          break
        case 'ar':
          const { payload } = await import('@/components/RouteComponents/Ars/actionPayload')
          data.conditionsMet = (customer.value && number)
          data.action = async () => this.$store.dispatch('fetchWithQuery', { ...payload, query: { customer: customer.value, number } })
          break
        case 'je':
          data.conditionsMet = !!number
          data.action = async () => this.$store.dispatch('fetchJesWithQuery', { number })
          break
        case 'pc':
          data.conditionsMet = !!number
          data.action = async () => this.$store.dispatch('fetchPcsWithQuery', { number })
      }

      if (data.conditionsMet && typeof data.action === 'function') {
        try {
          const resp = await data.action()
          if (!resp) {
            throw new Error('No response')
          }
          if (resp.hasOwnProperty('count') && resp.count) {
            const id = Object.values(resp.entities[`${name}s`])[0].id
            if (resp.count > 1 || this.originalHeader.id !== id) {
              this.trxAlreadyExists = true
              const h = this.$createElement
              const messageElements = [
                h('span', null, `A transaction with this ${name === 'ap' ? 'vendor and ' : ''}number already exists! `)
              ]
              switch (this.transactionType.name) {
                case 'pc':
                  break
                default:
                  messageElements.push(
                    h('button', {on: {click: () => this.openExistingTrx(id)}}, `Discard current & view existing ${name.toUpperCase()}`)
                  )
              }
              this.trxAlreadyExistsMsg = this.$message({
                message: h('p', null, messageElements),
                type: 'warning',
                duration: 0,
                showClose: true
              })
            }
          } else {
            this.trxAlreadyExists = false
            if (this.trxAlreadyExistsMsg) this.trxAlreadyExistsMsg.close()
          }
        } catch (e) {
          console.log(e)
        }
      }
    },
    closeTrxAlreadyExistsMsg () {
      if (this.trxAlreadyExistsMsg) this.trxAlreadyExistsMsg.close()
    },
    async getAppSettings () {
      const entryOptions = this.$store.getters.getUiSettings['TRANSACTION_ENTRY_OPTIONS']
      // For JEs, refresh the document numbering sequence (JE_NUMBER_SEQUENCE) so the
      // duplicate/save flows read the current configuration.
      if (this.transactionType.name === 'je') this.$store.dispatch('fetchDocSequenceSettings')
      if (entryOptions && JSON.parse(entryOptions)) {
        this.effectiveDateMandatory = JSON.parse(entryOptions).effective_date
        if (this.effectiveDateMandatory && this.form.effective_date === '' && (!this.form.hasOwnProperty('status') || ['DRAFT', 'OPEN'].includes(this.form.status))) {
          this.form.effective_date = this.effectiveDateMandatory ? getSimpleDate(new Date(), 'yyyy-mm-dd') : ''
        }
      }
    },
    /**
     * Get default data if they are not in store
     */
    async getDefaultData () {
      try {
        const {
          getAllSets,
          getAllLocations,
          getAllSeries,
          getAllFreefields,
          // getAllTaxcodes,
          getAllInsurances,
          getAllSourcecodes
        } = this.$store.getters

        let defaultDataPayloads = []
        if (!getAllSets.length) {
          defaultDataPayloads.push(setActionPayload)
        }
        if (!getAllLocations.length) {
          defaultDataPayloads.push(locationActionPayload)
        }
        if (!getAllSeries.length) {
          defaultDataPayloads.push(seriesActionPayload)
        }
        if (!getAllFreefields.length) {
          defaultDataPayloads.push(freefieldActionPayload)
        }
        // if (!getAllTaxcodes.length) {
        //   defaultDataPayloads.push(taxcodeActionPayload)
        // }
        if (!getAllInsurances.length) {
          defaultDataPayloads.push(insurancecodeActionPayload)
        }
        if (!getAllSourcecodes.length) {
          defaultDataPayloads.push(sourcecodeActionPayload)
        }
        let dataLoader = new DataLoader(this.$store, defaultDataPayloads, true, 200)
        dataLoader.loadDataBeforeRoute()
      } catch (e) {
        console.log(e)
      }
    }
  },
  computed: {
    ...mapGetters({
      series: 'getAllSeries',
      locations: 'getAllLocations',
      sets: 'getAllSets',
      taxcodes: 'getAllTaxcodes',
      freefields: 'getAllFreefields',
      billableitems: 'getAllBillableitem'
    }),
    /**
     * Returns the name of the work state for the currently selected vendor
     * @return {*}
     */
    vendorWorkState () {
      try {
        if (this.form.vendor && this.form.vendor.value) {
          const vendor = this.$store.getters.getVendorById(this.form.vendor.value)
          if (vendor && vendor.work_state) {
            return this.$store.getters.getStateById(vendor.work_state).name
          }
        }
        return ''
      } catch (e) {
        console.log(e)
        return ''
      }
    },
    /**
     * Returns all open POs for the currently selected vendor
     * @return {*}
     */
    vendorOpenPOs () {
      try {
        if (this.form.vendor && this.form.vendor.value) {
          const vendor = this.$store.getters.getVendorById(this.form.vendor.value)
          if (vendor && vendor.open_pos && Array.isArray(vendor.open_pos)) {
            return String(vendor.open_pos.length)
          }
        }
        return ''
      } catch (e) {
        console.log(e)
        return ''
      }
    },
    /**
     * Computed property that returns a boolean indicating whether the lines contain relief lines
     * @return Boolean
     */
    linesContainReliefLines () {
      return this.lines.some(line => {
        if (!line.form.relieves) {
          return false
        }
        return line.form.relieves.hasOwnProperty('value') ? !!line.form.relieves.value : !!line.form.relieves
      })
    },
    /**
     * Returns the name of the work state for the currently selected customer
     * @return {*}
     */
    customerWorkState () {
      try {
        if (this.form.customer && this.form.customer.value) {
          const customer = this.$store.getters.getCustomerById(this.form.customer.value)
          if (customer && customer.work_state) {
            return this.$store.getters.getStateById(customer.work_state).name
          }
        }
        return 'Not specified'
      } catch (e) {
        console.log(e)
        return ''
      }
    },
    /**
     * Returns attributes for the currently selected batch
     * @return {*}
     */
    batchAttributes () {
      const ret = { bank: null, company: null, production: null, currency: null }
      try {
        if (this.form.batch && this.form.batch.value) {
          const { value } = this.form.batch
          const { getBankByBatchId, getCompanyByBatchId, getProductionByBatchId, getCurrencyByBatchId, getBatchById, getSeriesById, getLocationById, getSetById, getFreefieldById, getTaxcodeById, getInsuranceById } = this.$store.getters
          if (getBankByBatchId(value) && getBankByBatchId(value).id) {
            ret.bank = getBankByBatchId(value)
          }
          if (getCompanyByBatchId(value) && getCompanyByBatchId(value).id) {
            ret.company = getCompanyByBatchId(value)
          }
          if (getProductionByBatchId(value) && getProductionByBatchId(value).id) {
            ret.production = getProductionByBatchId(value)
          }
          if (getCurrencyByBatchId(value) && getCurrencyByBatchId(value).id) {
            ret.currency = getCurrencyByBatchId(value)
          }

          const batch = getBatchById(this.form.batch.value)
          // Load optional default values in batch
          if (batch.default_series) {
            ret.series = getSeriesById(batch.default_series)
          }
          if (batch.hasOwnProperty('default_series') && (batch.default_series)) {
            ret.series = getSeriesById(batch.default_series)
          } else {
            ret.series = getSeriesById(1)
          }
          if (batch.hasOwnProperty('default_location') && (batch.default_location)) {
            ret.location = getLocationById(batch.default_location)
          } else {
            ret.location = getLocationById(1)
          }
          if (batch.hasOwnProperty('default_set') && (batch.default_set)) {
            ret.set = getSetById(batch.default_set)
          } else {
            ret.set = getSetById(1)
          }
          if (batch.hasOwnProperty('default_free_field_1') && (batch.default_free_field_1)) {
            ret.free_field_1 = getFreefieldById(batch.default_free_field_1)
          } else {
            ret.free_field_1 = {}
          }
          if (batch.hasOwnProperty('default_free_field_2') && (batch.default_free_field_2)) {
            ret.free_field_2 = getFreefieldById(batch.default_free_field_2)
          } else {
            ret.free_field_2 = {}
          }
          if (batch.hasOwnProperty('default_free_field_3') && (batch.default_free_field_3)) {
            ret.free_field_3 = getFreefieldById(batch.default_free_field_3)
          }
          if (batch.hasOwnProperty('default_free_field_4') && (batch.default_free_field_4)) {
            ret.free_field_4 = getFreefieldById(batch.default_free_field_4)
          }
          if (batch.hasOwnProperty('default_tax_code') && (batch.default_tax_code)) {
            ret.tax_code = getTaxcodeById(batch.default_tax_code)
          }
          if (batch.hasOwnProperty('default_insurance') && (batch.default_insurance)) {
            ret.insurance = getInsuranceById(batch.default_insurance)
          }
        }
        return ret
      } catch (e) {
        console.log(e)
        return ret
      }
    },
    /**
     * Computed property that returns a boolean indicating whether the user is permitted to add an asset
     * @return {*|boolean}
     */
    userCanAddAsset () {
      return this.$store.getters.checkIfUserHasRequiredPermissions(['system.add_asset'])
    },
    /**
     * Returns all postable accounts
     * @return {*}
     */
    postableAccounts () {
      return this.$store.getters.getAllAccounts.filter(account => account.postable)
    },
    /**
     * Returns the currency based on the value of the current batch
     * @return {*}
     */
    currency () {
      return this.$store.getters.getCurrencyByBatchId(this.form.batch.value)
    },
    /**
     * Returns the code for the current company, based on the value of the current batch
     * @return {*}
     */
    currentCompany () {
      const { getCompanyByBatchId } = this.$store.getters
      if (this.form.batch && this.form.batch.value && getCompanyByBatchId(this.form.batch.value)) {
        return getCompanyByBatchId(this.form.batch.value).code
      }
      return ''
    },
    /**
     * Returns the code for the current production, based on the value of the current batch
     * @return {*}
     */
    currentProduction () {
      const { getProductionByBatchId } = this.$store.getters
      if (this.form.batch && this.form.batch.value && getProductionByBatchId(this.form.batch.value)) {
        return getProductionByBatchId(this.form.batch.value).code
      }
      return ''
    },
    /**
     * Returns the line fields used for PRs (Journal Entries with source code of 'PR')
     * @return {*}
     */
    prLineFields () {
      return this.lineFields.filter(field => !['relieves', 'tax_code', 'asset', 'third_party_vendor'].includes(field.name))
    },
    /**
     * Returns a boolean indicating whether the distribution line amount fields should be read only. This depends on
     * the transaction's status, and whether the user is editing a PR that is not a draft
     */
    lineAmountsShouldBeReadOnly () {
      if (this.$route.name === 'EditPr') {
        return true
      } else {
        return ['POSTED', 'CLOSED', 'VOIDED', 'DELETED'].includes(this.form.status)
      }
    },
    /**
     * Returns a boolean indicating whether the distribution line relieves fields should be read only. This depends on
     * the transaction's status, and whether the user is editing a PR that is not a draft
     */
    lineRelievesShouldBeReadOnly () {
      if (['pc', 'je'].includes(this.transactionType.name)) {
        return ['POSTED', 'CLOSED', 'VOIDED', 'DELETED'].includes(this.form.status)
      } else {
        return !(this.form.vendor ? this.form.vendor ? this.form.vendor.value : false : false) || ['POSTED', 'CLOSED', 'VOIDED', 'DELETED'].includes(this.form.status)
      }
    },
    /**
     * Returns distribution line field definitions
     * @return {*}
     */
    lineFields () {
      const { name } = this.transactionType
      const relievesActionPayload = {searchableFields: ['meta_name', 'description'], noApi: true}
      const fields = [
        {
          name: 'line_number',
          label: 'Line',
          dataType: 'number',
          fieldType: 'input',
          disabled: true,
          fieldWidth: '2%'
        },
        {
          name: 'company',
          label: 'Co',
          dataType: 'string',
          fieldType: 'display',
          displayVal: this.currentCompany,
          fieldWidth: '4%',
          hidden: true
        },
        {
          name: 'production',
          label: 'Prod',
          dataType: 'string',
          fieldType: 'display',
          displayVal: this.currentProduction,
          fieldWidth: '4%',
          hidden: true
        },
        {
          name: 'relieves',
          label: 'PO',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: name && !['pc', 'je'].includes(name) ? () => new AutocompleteLocalSearch(this.vendorPoLines, relievesActionPayload).createOptions() : null,
          searchAsync: ['pc', 'je'].includes(name),
          remote: ['pc', 'je'].includes(name),
          searchOpts: !['pc', 'je'].includes(name)
                      ? null
                      : { endpoint: 'lines/po', query: { transaction__status: 'POSTED', type__in: 'OPEN,PARTIAL' } },
          placeholder: 'PO Line',
          fieldWidth: '7%',
          readOnly: ['EditPr', 'NewPr'].includes(this.$route.name) ? this.lineAmountsShouldBeReadOnly : this.lineRelievesShouldBeReadOnly
        },
        {
          name: 'account',
          label: 'Account',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.postableAccounts, accountActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '6%',
          readOnly: this.readOnly,
          actionPayload: accountActionPayload
        },
        {
          name: 'account:name',
          label: 'Account Name',
          dataType: 'string',
          fieldType: 'display',
          displayVal: (accountId) => {
            const acct = this.$store.getters.getAccountById(accountId)
            return acct && acct.id ? acct.name : ''
          },
          fieldWidth: '8%',
          hidden: false
        },
        {
          name: 'description',
          label: 'Description',
          dataType: 'string',
          fieldType: 'input',
          required: true,
          fieldWidth: '19%',
          readOnly: this.readOnly
        },
        {
          name: 'other_name',
          label: 'Other Name',
          dataType: 'string',
          fieldType: 'input',
          required: false,
          fieldWidth: '6%',
          hidden: this.transactionType.name !== 'pc',
          readOnly: this.readOnly
        },
        {
          name: 'amount_decimal',
          label: 'Amount',
          dataType: 'string',
          fieldType: 'money',
          required: true,
          fieldWidth: '8%',
          readOnly: this.lineAmountsShouldBeReadOnly
        },
        {
          name: 'location',
          label: 'Loc',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.locations, locationActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '4%',
          readOnly: this.readOnly,
          actionPayload: locationActionPayload
        },
        {
          name: 'series',
          label: 'Series',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.series, seriesActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '5%',
          readOnly: this.readOnly,
          actionPayload: seriesActionPayload
        },
        {
          name: 'set',
          label: 'Set',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.sets, setActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '5%',
          readOnly: this.readOnly,
          actionPayload: setActionPayload
        },
        {
          name: 'tax_code',
          label: 'Tax code',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.taxcodes, taxcodeActionPayload).createOptions(),
          remote: false,
          fieldWidth: '5%',
          readOnly: this.readOnly,
          actionPayload: taxcodeActionPayload
        },
        {
          name: 'free_field_1',
          label: 'FF 1',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '4%',
          readOnly: this.readOnly,
          actionPayload: freefieldActionPayload
        },
        {
          name: 'free_field_2',
          label: 'FF 2',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '4%',
          readOnly: this.readOnly,
          actionPayload: freefieldActionPayload
        },
        {
          name: 'free_field_3',
          label: 'FF 3',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '4%',
          hidden: true,
          readOnly: this.readOnly,
          actionPayload: freefieldActionPayload
        },
        {
          name: 'free_field_4',
          label: 'FF 4',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '4%',
          hidden: true,
          readOnly: this.readOnly,
          actionPayload: freefieldActionPayload
        },
        {
          name: 'asset',
          label: 'Asset',
          dataType: 'boolean',
          fieldType: 'checkbox',
          fieldWidth: '2%',
          readOnly: this.readOnly || !this.userCanAddAsset
        },
        {
          name: 'third_party_vendor',
          label: '3rd',
          dataType: 'string',
          fieldType: 'autocomplete',
          searchAsync: true,
          searchOpts: { endpoint: 'vendors', query: {} },
          remote: true,
          fieldWidth: '5%',
          readOnly: this.readOnly
        },
        {
          name: 'is_offset',
          label: 'Offset',
          dataType: 'boolean',
          fieldType: 'checkbox',
          hidden: true
        }
      ]
      return this.setCustomFieldOrder(fields)
    },
     /**
     * Returns attributes for the series, location and set of selected bank
     * @return {*}
     */
    bankAttributes () {
      const ret = { series: null, location: null, set: null }
      try {
        if (this.form.batch && this.form.batch.value) {
          const { getBankByBatchId, getSeriesById, getLocationById, getSetById } = this.$store.getters
          const bank = getBankByBatchId(this.form.batch.value)
          // Load optional default values in bank
          if (bank) {
            if (bank.hasOwnProperty('series') && (bank.series)) {
              ret.series = getSeriesById(bank.series)
            }
            if (bank.hasOwnProperty('location') && (bank.location)) {
              ret.location = getLocationById(bank.location)
            }
            if (bank.hasOwnProperty('set') && (bank.set)) {
              ret.set = getSetById(bank.set)
            }
          }
        }
        return ret
      } catch (e) {
        console.log(e)
        return ret
      }
    }
  },
  created () {
    this.getAppSettings()
    this.getDefaultData()
    // this.$store.dispatch('clearAccountState', true)
    accountActionPayload.endpoint = 'users/department_accounts'
  }
}



// WEBPACK FOOTER //
// ./src/Mixins/Transactions.js